'use strict';

/**
 * emitSaveForce(context) — the Manager-side live push on app save.
 *
 * Extracted from edit-app.js saveChanges() (#611), which emitted
 * `app:<appId>:force` only when `this.dynamic` held a fetched preview feed.
 * Since b2f2f49b (#592) primes the preview's dynamicFetchTracker with the
 * SAVED app at editor open, the feed fetch — the primary writer of
 * `this.dynamic` (a live `app:<id>:dynamic` WS push also updates it) — no
 * longer runs for presentation-only edits, so that guard
 * silently dropped the push for every Workday edit and every Achievers edit
 * except whatToPlay changes. The fix, for slugs on the allowlist below: send
 * the completed preview feed when one was fetched (Achievers/Workday feeds
 * are object manifests with no .length, so they need their own branch); for a
 * presentation-only edit (`dynamic` still at its initial null) send the SLS
 * snapshot taken at editor open, provided its feed collection is non-empty;
 * otherwise fall back to a cache-less force. The `dynamic == null` gate
 * matters: a feed-selection change writes [] before refetching, and from that
 * point the editor-open snapshot describes the PREVIOUS selection — pushing
 * it would pair fresh data with a stale body and blank the player.
 *
 * Why a slug allowlist and not `appTemplate.type === 'integration'`: the
 * Manager payload is a compatibility contract, not a type-system contract.
 * These six dynamic-feed players explicitly preserve their last feed when a
 * force envelope has no cache; every other app retains the conservative
 * cache-carrying-only behavior.
 */
var CACHELESS_FORCE_SAFE_SLUGS = [
    'achievers',
    'workday',
    'twitter-hashtag',
    'twitter-timeline',
    'dropbox-gallery',
    'message-board',
];

// The collections the allowlisted players build slides from: Achievers
// manifests carry `items`, Workday manifests carry `jobs` (the other four
// slugs store plain arrays). A structurally valid body whose collection is
// empty renders the player's empty state, so pushing one can only blank a
// sign that a cache-less force would have left playing.
var FEED_COLLECTION_KEYS = ['items', 'jobs'];

function hasPlayableFeedBody(body) {
    if (Array.isArray(body)) return body.length > 0;
    if (!body || typeof body !== 'object' || Object.keys(body).length === 0) return false;
    return FEED_COLLECTION_KEYS.every(function (key) {
        return !Array.isArray(body[key]) || body[key].length > 0;
    });
}

function emitSaveForce(context) {
    var slug = context.appTemplate && context.appTemplate.slug;
    var cachelessSafe = CACHELESS_FORCE_SAFE_SLUGS.indexOf(slug) !== -1;
    var payload = {
        data: context.data,
        settings: context.settings,
    };

    if (context.dynamic && context.dynamic.length) {
        payload.cache = context.dynamic;
    } else if (cachelessSafe && hasPlayableFeedBody(context.dynamic)) {
        // A completed Achievers/Workday preview fetch resolves to an object
        // manifest with no .length. It describes the selection being
        // persisted, so it must win over the editor-open snapshot below.
        payload.cache = context.dynamic;
    } else if (cachelessSafe && context.dynamic == null && hasPlayableFeedBody(context.cache)) {
        // A presentation-only edit deliberately does not refetch an
        // integration feed, leaving `dynamic` at its initial null — only then
        // does the SLS snapshot taken at editor open still match the
        // persisted selection. A feed-selection change writes [] before
        // refetching, so while that fetch is in flight or failed, degrade to
        // a cache-less force rather than pair new data with the old body.
        payload.cache = context.cache;
    }

    if (!payload.cache && !cachelessSafe) return false;

    context.WS.event.emit('app:' + context.appId + ':force', payload);
    return true;
}

// The live player must only receive the save after persistence succeeds.
// Otherwise a rejected PATCH leaves the player showing state the Manager did
// not actually save, which will be overwritten on its next reload.
function emitSaveForceAfterPersist(context) {
    return context.persist().then(function () {
        return emitSaveForce(context);
    });
}

if (typeof module !== 'undefined' && typeof exports !== 'undefined' && module.exports === exports) {
    module.exports = {
        CACHELESS_FORCE_SAFE_SLUGS: CACHELESS_FORCE_SAFE_SLUGS,
        emitSaveForce: emitSaveForce,
        emitSaveForceAfterPersist: emitSaveForceAfterPersist,
    };
}

if (typeof angular !== 'undefined') {
    angular.module('rocket.components').value('saveForceEmit', {
        CACHELESS_FORCE_SAFE_SLUGS: CACHELESS_FORCE_SAFE_SLUGS,
        emitSaveForce: emitSaveForce,
        emitSaveForceAfterPersist: emitSaveForceAfterPersist,
    });
}
